import React from 'react';
import './AccentDiagnosticTable.css';

interface AccentResult {
  accent: string;
  flag: string;
  transcript: string;
  confidence: number;
  status: 'pending' | 'processing' | 'complete' | 'error';
}

interface AccentDiagnosticTableProps {
  results: AccentResult[];
  targetSentence?: string;
  recommendedAccent?: string;
}

const AccentDiagnosticTable: React.FC<AccentDiagnosticTableProps> = ({
  results,
  targetSentence,
  recommendedAccent
}) => {
  // Calculate word match percentage if target sentence provided
  const calculateMatchPercentage = (transcript: string): number => {
    if (!targetSentence || !transcript) return 0;
    
    const targetWords = targetSentence.toLowerCase().replace(/[.,!?;:'"]/g, '').split(/\s+/);
    const transcriptWords = transcript.toLowerCase().replace(/[.,!?;:'"]/g, '').split(/\s+/);
    
    let matches = 0;
    const maxLength = Math.max(targetWords.length, transcriptWords.length);
    
    for (let i = 0; i < Math.min(targetWords.length, transcriptWords.length); i++) {
      if (targetWords[i] === transcriptWords[i]) {
        matches++;
      }
    }
    
    return Math.round((matches / maxLength) * 100);
  };
  
  // Sort results by confidence/accuracy
  const sortedResults = [...results].sort((a, b) => {
    if (a.status !== 'complete' || b.status !== 'complete') {
      return a.status === 'complete' ? -1 : 1;
    }
    
    const matchA = calculateMatchPercentage(a.transcript);
    const matchB = calculateMatchPercentage(b.transcript);
    
    // Sort by match percentage first, then confidence
    if (matchA !== matchB) {
      return matchB - matchA;
    }
    return b.confidence - a.confidence;
  });
  
  // Find best performing accent
  const bestAccent = sortedResults.find(r => r.status === 'complete');
  const bestMatch = bestAccent ? calculateMatchPercentage(bestAccent.transcript) : 0;
  
  return (
    <div className="accent-diagnostic-table">
      <div className="diagnostic-header">
        <h3>🔍 Speech Recognition Diagnostic</h3>
        <p className="diagnostic-subtitle">
          Testing your speech against multiple English accent models to find the best match
        </p>
      </div>
      
      {targetSentence && (
        <div className="target-reference">
          <strong>Target:</strong> <span className="target-text">{targetSentence}</span>
        </div>
      )}
      
      <div className="diagnostic-results">
        <table className="accent-table">
          <thead>
            <tr>
              <th>Accent</th>
              <th>Recognized Text</th>
              <th>Match</th>
              <th>Confidence</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {sortedResults.map((result, index) => {
              const matchPercentage = calculateMatchPercentage(result.transcript);
              const isBest = bestAccent && result.accent === bestAccent.accent;
              const isRecommended = recommendedAccent && result.accent === recommendedAccent;
              
              return (
                <tr 
                  key={result.accent}
                  className={`
                    ${isBest ? 'best-match' : ''}
                    ${isRecommended ? 'recommended' : ''}
                    ${result.status === 'error' ? 'error-row' : ''}
                  `}
                >
                  <td className="accent-cell">
                    <span className="accent-flag">{result.flag}</span>
                    <span className="accent-name">{result.accent}</span>
                    {isBest && <span className="best-badge">BEST</span>}
                    {isRecommended && <span className="recommended-badge">Selected</span>}
                  </td>
                  <td className="transcript-cell">
                    {result.status === 'complete' ? (
                      <div className="transcript-text">
                        {result.transcript || '(no speech detected)'}
                      </div>
                    ) : result.status === 'processing' ? (
                      <span className="processing">Processing...</span>
                    ) : result.status === 'error' ? (
                      <span className="error">Error</span>
                    ) : (
                      <span className="pending">Waiting...</span>
                    )}
                  </td>
                  <td className="match-cell">
                    {result.status === 'complete' && targetSentence && (
                      <div className="match-indicator">
                        <div className="match-bar">
                          <div 
                            className="match-fill"
                            style={{
                              width: `${matchPercentage}%`,
                              backgroundColor: matchPercentage > 80 ? '#4CAF50' : 
                                             matchPercentage > 60 ? '#FFC107' : 
                                             '#f44336'
                            }}
                          />
                        </div>
                        <span className="match-percentage">{matchPercentage}%</span>
                      </div>
                    )}
                  </td>
                  <td className="confidence-cell">
                    {result.status === 'complete' && (
                      <span className={`confidence-value ${
                        result.confidence > 0.8 ? 'high' : 
                        result.confidence > 0.6 ? 'medium' : 'low'
                      }`}>
                        {Math.round(result.confidence * 100)}%
                      </span>
                    )}
                  </td>
                  <td className="status-cell">
                    <span className={`status-indicator status-${result.status}`}>
                      {result.status === 'complete' && '✓'}
                      {result.status === 'processing' && '⟳'}
                      {result.status === 'error' && '✗'}
                      {result.status === 'pending' && '⋯'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {bestAccent && bestMatch > 0 && (
        <div className="diagnostic-summary">
          <div className="summary-box">
            <h4>Recommendation</h4>
            <p>
              Best recognition with <strong>{bestAccent.accent}</strong> ({bestMatch}% match).
              {recommendedAccent !== bestAccent.accent && (
                <span className="recommendation-note">
                  {' '}Consider switching from {recommendedAccent} to improve accuracy.
                </span>
              )}
            </p>
          </div>
          
          <div className="diagnostic-tips">
            <h4>Tips for Better Recognition:</h4>
            <ul>
              {bestMatch < 50 && (
                <>
                  <li>Speak more slowly and clearly</li>
                  <li>Ensure microphone is close to your mouth</li>
                  <li>Reduce background noise</li>
                </>
              )}
              {bestMatch >= 50 && bestMatch < 80 && (
                <>
                  <li>Try enunciating consonants more clearly</li>
                  <li>Maintain consistent volume</li>
                </>
              )}
              {bestMatch >= 80 && (
                <li>Recognition is working well! Keep practicing with this accent setting.</li>
              )}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccentDiagnosticTable;